import React, { useState, useMemo, useCallback } from "react";
import { Collapse } from "antd";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { ApolloError } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import ChatWindow from "components/chat-window/chat-window";
import MediaPlayer from "components/media-player/media-player";
import StatusIndicator from "components/status-indicator/status-indicator";
import DropdownArrow from "components/dropdown-arrow/dropdown-arrow";
import NotificationsList from "components/notification-list/notification-list";

import { AppBrand, MediaType, UserRoleValue } from "constants/field-values";
import routeNames from "constants/route-names";
import { getPropertyAddress } from "utils/helpers";

import { Property_Documents } from "graphql/generated/graphql";
import {
	type GetUserPropertiesForDashboardQuery as UserPropsQueryType,
	useGetUserPropertiesForDashboardQuery
} from "graphql/queries/get-user-properties-for-dashboard.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useInstalledProductsFiltersStore } from "state/zustand-stores/installed-products-filters-store";

import { useBrand } from "context/BrandContext";

const Dashboard: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();
	const navigate = useNavigate();

	const [myProperties, setMyProperties] = useState<Array<any>>([]);
	const [dashboardDeviceStatusMap, setDashboardDeviceStatusMap] = useState<Map<string, number>>(new Map());
	const [dashboardStatusProductIdsMap, setDashboardStatusProductIdsMap] = useState<Map<string, Record<string, string[]>>>(new Map());
	const [dashboardStatusInstalledDevIdsMap, setDashboardStatusInstalledDevIdsMap] = useState<Map<string, Record<string, { online: string[]; offline: string[] }>>>(new Map());
	const [mySelectedPropertyKey, setMySelectedPropertyKey] = useState<string>("");
	const [fileViewer, setFileViewer] = useState<{
		type: MediaType | null;
		src: string;
	}>({ type: null, src: "" });
	const { brand } = useBrand();
	const isBrandHighnotes = brand === AppBrand.Highnote;

	const isUserBasic = loggedUserValue?.role === UserRoleValue.basic;

	const setFilters = useInstalledProductsFiltersStore(state => state.setFilters);

	const handleOnOpenDocument = useCallback(async (document: any) => {
		if (document.document_link) {
			try {
				setFileViewer({
					type: MediaType.Document,
					src: document.document_link
				});
			} catch (error) {
				console.log("handleOnOpenDocument error: ", error);
			} finally {
				return;
			}
		}

		if (document.video_link) {
			setFileViewer({ type: MediaType.Video, src: document.video_link });
			return;
		}

		if (document.photo_link) {
			setFileViewer({ type: MediaType.Image, src: document.photo_link });
			return;
		}
	}, []);

	const handleOnErrorGetUserProperties = useCallback((error: ApolloError) => {
		// NetworkStatusContext automatically handles this error through Apollo error link
		console.log("useGetUserPropertiesForDashboardQuery Error: ", error);
	}, []);

	const handleOnCompleteGetUserProperties = useCallback((data: UserPropsQueryType) => {
		// NetworkStatusContext automatically tracks successful operations

		const { user_properties } = data || {};

		if (user_properties.length > 0) {
			const deviceStatusByPropertyMap = new Map();
			const statusProductIdsByPropertyMap = new Map();
			const statusInstalledDevIdsByPropertyMap = new Map<string, Record<string, { online: string[]; offline: string[] }>>();

			const formattedProperties = user_properties.reduce((acc, element) => {
				if (element.property) {
					const { id, name, address, standardized_address, photo_link, property_documents, installed_products } =
						element.property;

					const myProp: any = {
						user_property_id: element.id,
						property_id: id,
						name,
						address,
						standardized_address,
						photo_link,
						property_documents,
						installed_products_by_type: []
					};

					if (installed_products?.length) {
						const productCategoryMap = new Map();
						const grouped = {};
						const deviceStatusByCategory = {};
						const statusProductIdsByCategory: Record<string, string[]> = {};
						const statusInstalledDevIdsByCategory: Record<string, { online: string[]; offline: string[] }> = {};

						for (const installed_prod of installed_products) {
							const productCategory = installed_prod.product?.product_category;
							if (!productCategory) continue;
							const type = productCategory.label || "Unknown";
							grouped[type] = (grouped[type] || 0) + 1;

							if (!productCategoryMap.has(productCategory.label)) {
								productCategoryMap.set(productCategory.label, productCategory.name);
							}
							if (installed_prod.device_status) {
								if (!deviceStatusByCategory[type]) {
									deviceStatusByCategory[type] = installed_prod.device_status === "online" ? 1 : -1;
								} else if (installed_prod.device_status === "online" && deviceStatusByCategory[type] > 0) {
									deviceStatusByCategory[type]++;
								} else if (installed_prod.device_status === "offline") {
									if (deviceStatusByCategory[type] > 0) {
										deviceStatusByCategory[type] = -1;
									} else {
										deviceStatusByCategory[type]--;
									}
								}

								// Store unique product IDs for monitored devices in this category.
								// This array helps determine if all devices in a category belong to the same product (length === 1) or different products (length > 1).
								const productId = installed_prod.product?.id;
								if (productId) {
									if (!statusProductIdsByCategory[type]) {
										statusProductIdsByCategory[type] = [];
									}
									if (!statusProductIdsByCategory[type].includes(productId)) {
										statusProductIdsByCategory[type].push(productId);
									}
								}

								// Store installed device IDs grouped by status to support single-device navigation.
								const installedDevId = installed_prod.id;
								if (installedDevId) {
									if (!statusInstalledDevIdsByCategory[type]) {
										statusInstalledDevIdsByCategory[type] = { online: [], offline: [] };
									}
									const statusKey = installed_prod.device_status as "online" | "offline";
									statusInstalledDevIdsByCategory[type][statusKey].push(installedDevId);
								}
							}
						}

						const installedProductPerProductType = Object.entries(grouped).map(([label, count]) => ({
							type: { name: productCategoryMap.get(label), label: label },
							count
						}));

						deviceStatusByPropertyMap.set(id, deviceStatusByCategory);
						statusProductIdsByPropertyMap.set(id, statusProductIdsByCategory);
						statusInstalledDevIdsByPropertyMap.set(id, statusInstalledDevIdsByCategory);
						myProp.installed_products_by_type = installedProductPerProductType || [];
					}

					acc.push(myProp);
				}
				return acc;
			}, [] as any[]);

			setDashboardDeviceStatusMap(deviceStatusByPropertyMap);
			setDashboardStatusProductIdsMap(statusProductIdsByPropertyMap);
			setDashboardStatusInstalledDevIdsMap(statusInstalledDevIdsByPropertyMap);
			setMyProperties(formattedProperties);
		}
	}, []);

	useGetUserPropertiesForDashboardQuery({
		skip: !loggedUserValue.id || isUserBasic,
		variables: {
			where: {
				user_id: { _eq: loggedUserValue.id },
				property: { deleted_at: { _is_null: true } }
			}
		},
		errorPolicy: "all",
		fetchPolicy: "cache-and-network",
		nextFetchPolicy: "cache-and-network",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorGetUserProperties,
		onCompleted: handleOnCompleteGetUserProperties
	});

	const showProperties = !isUserBasic && myProperties?.length > 0;

	const myPropertiesElements = useMemo(() => {
		if (showProperties) {
			let itemsToDisplay: any[] = [];
			if (mySelectedPropertyKey) {
				itemsToDisplay = myProperties.filter(element => {
					const currentKey = `my-property-${element.user_property_id}`;
					const isSelected = currentKey === mySelectedPropertyKey;
					return isSelected;
				});
			} else {
				itemsToDisplay = [...myProperties];
			}

			const items = itemsToDisplay.map(property => {
				const currentKey = `my-property-${property.user_property_id}`;
				const isSelected = currentKey === mySelectedPropertyKey;
				const propertyDeviceStatuses = dashboardDeviceStatusMap.get(property.property_id);
				let overallDeviceStatusCount: number = 0;
				let overallDeviceStatus: string | undefined;

				if (propertyDeviceStatuses) {
					overallDeviceStatusCount = Object.values(propertyDeviceStatuses).reduce((acc, curr) => {
						if (acc === 0 || (acc < 0 && curr < 0) || (acc > 0 && curr >= 0)) {
							return acc + curr;
						} else if (acc > 0 && curr < 0) {
							return curr;
						}
						return acc;
					}, 0);
				}

				if (overallDeviceStatusCount) {
					overallDeviceStatus = overallDeviceStatusCount > 0 ? "online" : "offline";
				}

				return {
					key: currentKey,
					label: (
						<div className={"my-property-header-container"}>
							{isSelected ? (
								<IoIosArrowBack size={19} color={"#404149"} style={{ marginRight: "16px" }} />
							) : property.photo_link ? (
								<img src={property.photo_link} className="my-property-header-img" />
							) : (
								<div className="my-property-header-img cflex">
									<img src={`${process.env.PUBLIC_URL}/assets/images/icons/property-placeholder.svg`} height={19} />
								</div>
							)}
							<span className="my-property-header-name">
								<span>{property.name}</span>
								{overallDeviceStatus && (
									<StatusIndicator deviceStatus={overallDeviceStatus} count={Math.abs(overallDeviceStatusCount)} />
								)}
							</span>
						</div>
					),
					showArrow: !isSelected,
					children: (
						<div className="my-property-panel-container">
							<div className={"property-panel-row"}>
								<span className="my-property-address">
									{getPropertyAddress(property.address, property.standardized_address)}
								</span>
								{property.photo_link ? (
									<img src={property.photo_link} className="my-property-img" />
								) : (
									<div className="my-property-img cflex">
										<img src={`${process.env.PUBLIC_URL}/assets/images/icons/property-placeholder.svg`} height={40} />
									</div>
								)}
							</div>
							<Collapse
								expandIconPosition="end"
								bordered={false}
								className="my-property-collapse-container collapse-container-resources"
								expandIcon={({ isActive }) => <DropdownArrow isActive={isActive} />}
								items={[
									{
										key: `my-properties-documents-${property.user_property_id}`,
										label: "RESOURCES",
										children: (
											<div>
												{property.property_documents.map((doc: Property_Documents) => {
													const onClickAttachment = () => handleOnOpenDocument(doc);

													return (
														<div key={doc.id} className="my-property-document" onClick={onClickAttachment}>
															<img
																src={`${process.env.PUBLIC_URL}/assets/images/icons/property-document-red.svg`}
																className="my-property-document-icon"
															/>
															<span className="my-property-document-label">{doc.name || doc.description}</span>
														</div>
													);
												})}
											</div>
										)
									}
								]}
								defaultActiveKey={[`my-properties-documents-${property.user_property_id}`]}
							/>
							<Collapse
								expandIconPosition="end"
								bordered={false}
								className="my-property-collapse-container collapse-container-devices"
								expandIcon={({ isActive }) => <DropdownArrow isActive={isActive} />}
								items={[
									{
										key: `my-properties-installed-products-${property.user_property_id}`,
										label: (
											<div>
												DEVICES
												<div className={`status-dot ${overallDeviceStatus}`}></div>
											</div>
										),
										children: (
											<div>
												{property.installed_products_by_type.map(({ type, count }) => {
													const statusCountForType = propertyDeviceStatuses?.[type.label];

													return (
														<span
															className="my-property-product-label"
															key={`my-properties-installed-products-${property.user_property_id}-${type.name}`}
														>
															<span>
																{`${type.label} (`}
																<strong
																	onClick={() => {
																		setFilters({
																			selectedPropertiesFilter: [property.property_id],
																			selectedCategoriesFilter: [type.name],
																			selectedProductsFilter: [],
																			selectedDeviceStatusFilter: [],
																			selectedWarrantyStatusFilter: [],
																			installationDateStart: null,
																			installationDateEnd: null,
																			searchInstalledProductsTerm: "",
																			gridView: false,
																		});
																		navigate(routeNames.INSTALLED_DEVICES);
																	}}
																>
																	{count}
																</strong>
																{`)`}
															</span>
															{statusCountForType && (
																<div
																	style={{ cursor: "pointer" }}
																	onClick={() => {
																		const statusKey = statusCountForType > 0 ? "online" : "offline";
																		const installedDevIds = dashboardStatusInstalledDevIdsMap.get(property.property_id)?.[type.label]?.[statusKey] || [];
																		const productIds = dashboardStatusProductIdsMap.get(property.property_id)?.[type.label] || [];

																		if (installedDevIds.length === 1) {
																			// Single device with this status: navigate directly to its detail page
																			navigate(routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", installedDevIds[0]));
																		} else if (productIds.length > 0) {
																			// Multiple devices: navigate to products list and filter them
																			setFilters({
																				selectedPropertiesFilter: [property.property_id],
																				selectedCategoriesFilter: [type.name],
																				selectedProductsFilter: productIds,
																				selectedDeviceStatusFilter: [statusKey],
																				selectedWarrantyStatusFilter: [],
																				installationDateStart: null,
																				installationDateEnd: null,
																				searchInstalledProductsTerm: "",
																				gridView: false,
																			});
																			navigate(routeNames.INSTALLED_DEVICES);
																		}
																	}}
																>
																	<StatusIndicator
																		deviceStatus={statusCountForType > 0 ? "online" : "offline"}
																		count={Math.abs(statusCountForType)}
																		transparent
																	/>
																</div>
															)}
														</span>
													);
												})}
											</div>
										)
									}
								]}
								defaultActiveKey={[`my-properties-installed-products-${property.user_property_id}`]}
							/>
						</div>
					)
				};
			});

			return (
				<Collapse
					expandIconPosition="end"
					className="my-property-collapse-container collapse-container-properties"
					accordion={true}
					bordered={false}
					defaultActiveKey={["1"]}
					expandIcon={({ isActive }) => {
						if (isActive) {
							return <IoIosArrowBack size={19} color={"#404149"} />;
						} else {
							return <IoIosArrowForward size={19} color={"#404149"} />;
						}
					}}
					style={{ background: "white" }}
					items={items}
					onChange={(key: string | string[]) => {
						setMySelectedPropertyKey(key[0]);
					}}
				/>
			);
		} else {
			return (
				<div className="property-list-empty">
					<img src={`${process.env.PUBLIC_URL}/assets/images/icons/property-in-circle.svg`} />
					<span>Your properties will appear here.</span>
				</div>
			);
		}
	}, [
		myProperties,
		handleOnOpenDocument,
		mySelectedPropertyKey,
		setFilters,
		navigate,
		dashboardDeviceStatusMap,
		dashboardStatusProductIdsMap,
		dashboardStatusInstalledDevIdsMap,
		showProperties
	]);

	return (
		<div className="dashboard-container">
			<div className="dashboard-content-container">
				<div className="dashboard-chat-container">
					<span className="dashboard-title">{isBrandHighnotes ? "HiGnome AI Assistant" : "Maestro AI Assistant"}</span>
					<ChatWindow isDashboard={true} />
				</div>
				<div className="dashboard-right-container">
					<div className={`dashboard-properties-container${showProperties ? "" : " empty-container"}`}>
						<span className="dashboard-title">My Properties</span>
						<div className="dashboard-card properties">{myPropertiesElements}</div>
					</div>
					<NotificationsList isDashboard />
				</div>
			</div>

			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>
		</div>
	);
};

export default Dashboard;
